import React, { Fragment, useEffect, useState } from 'react';
import purify from 'dompurify';
import { Grid, Text } from 'elements';
import { TCase } from 'modules/case/types';
import { CWindowWidth } from 'constants/base.const';
import { Button } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { Colors } from 'GlobalStyles';

type TQuestionsParams = {
  item: TCase;
};

const Questions = ({ item }: TQuestionsParams) => {
  const [isClarification, setIsClarification] = useState(false);
  const [selectedClarification, setSelectedClarification] = useState<{
    [key: string]: boolean;
  }>();
  const [selectedQuestion, setSelectedQuestion] = useState<{
    [key: string]: boolean;
  }>();

  useEffect(() => {
    if (item) {
      const selectedClarification: { [key: string]: boolean } = {};
      item.clarification?.forEach((i, index) => {
        selectedClarification[index] = false;
      });
      const selectedQuestion: { [key: string]: boolean } = {};
      item.additional_question?.forEach((i, index) => {
        selectedQuestion[index] = false;
      });
      setSelectedClarification(selectedClarification);
      setSelectedQuestion(selectedQuestion);
    }
  }, []);

  return (
    <Grid
      maxWidth={`${CWindowWidth}px`}
      padding='35px 50px'
      md={{ padding: '35px 20px' }}
      sm={{ padding: '16px' }}
    >
      <Grid gap='35px 15px' sm={{ padding: '0 5px', gap: '10px', justify: 'space-between' }}>
        <Grid isInline width='23%' sm={{ width: '100%' }}>
          <Text size='2rem' sm={{ size: '1.6rem' }}>
            Case 질문
          </Text>
        </Grid>
        <Grid isInline width='calc(77% - 15px)' sm={{ width: '100%' }}>
          <Text size='1.1rem'>{item.question_content}</Text>
        </Grid>
      </Grid>
      <Grid gap='35px 15px' marginTop='20px' sm={{ padding: '0 5px', gap: '10px', justify: 'space-between' }}>
        <Grid isInline width='23%' sm={{ width: '60%' }}>
          <Text size='2rem' sm={{ size: '1.6rem' }}>
            Clarification
          </Text>
        </Grid>
        {isClarification && (
          <Grid display='none' sm={{ display: 'block', isInline: true, width: 'auto' }}>
            <Button className='small outline' onClick={() => setIsClarification(false)}>
              추가정보 닫기
            </Button>
          </Grid>
        )}
        <Grid isInline width='calc(77% - 15px)' sm={{ width: isClarification ? '100%' : '80px' }}>
          {!isClarification ? (
            <>
              <Grid marginTop='5px' sm={{ display: 'none' }}>
                <Text size='1.1rem'>
                  문제상황을 구체화 하기 위해 어떤 질문을 할지 생각해 보시고, 준비가 되면 ‘추가정보 보기’ 를
                  눌러주세요.
                </Text>
              </Grid>
              <Grid marginTop='5px'>
                <Button className='small outline' onClick={() => setIsClarification(true)}>
                  추가정보 보기
                </Button>
              </Grid>
            </>
          ) : (
            <>
              <Grid padding='5px 0' sm={{ display: 'none' }}>
                <Text size='1.1rem'>직접 생각했던 이슈 중심으로 내용을 확인해 보세요,</Text>
              </Grid>
              <Grid
                padding='0 0 10px 0'
                marginTop='5px'
                style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                sm={{ display: 'none' }}
              >
                <Button className='small outline' onClick={() => setIsClarification(false)}>
                  추가정보 닫기
                </Button>
              </Grid>
              {!!item.clarification &&
                item.clarification.map((c, index) => (
                  <Grid
                    key={index}
                    marginTop='5px'
                    padding='5px 0'
                    align='center'
                    gap='0 15px'
                    style={{ borderBottom: `1px solid ${Colors.grey1}` }}
                  >
                    <Text size='1.1rem' bold={selectedClarification && selectedClarification[index]}>
                      {c.title}
                    </Text>
                    {selectedClarification && (
                      <>
                        {selectedClarification && selectedClarification[index] ? (
                          <Button
                            className='text'
                            icon={<MinusOutlined />}
                            onClick={() =>
                              setSelectedClarification({
                                ...selectedClarification,
                                [index]: false,
                              })
                            }
                          />
                        ) : (
                          <Button
                            className='text'
                            icon={<PlusOutlined />}
                            onClick={() =>
                              setSelectedClarification({
                                ...selectedClarification,
                                [index]: true,
                              })
                            }
                          />
                        )}
                        {selectedClarification && selectedClarification[index] && (
                          <Grid marginTop='5px' padding='5px 0'>
                            <Text color={Colors.grey3}>{c.content}</Text>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                ))}
            </>
          )}
        </Grid>
      </Grid>
      {!!item.additional_question && item.additional_question.length > 0 && (
        <Grid
          gap='25px 15px'
          marginTop='35px'
          sm={{
            padding: '0 5px',
            marginTop: '15px',
            justify: 'space-between',
            gap: '15px 15px',
          }}
        >
          {item.additional_question.map((q, index) => (
            <Fragment key={index}>
              <Grid isInline width='23%' sm={{ width: '60%' }}>
                <Text size='2rem' sm={{ size: '1.6rem' }}>
                  추가 질문 {index + 1}
                </Text>
              </Grid>
              <Grid isInline width='calc(77% - 15px)' marginTop='8px' sm={{ width: '62px', marginTop: '0' }}>
                {selectedQuestion && (
                  <>
                    <Button
                      className='small outline'
                      style={{ height: 20 }}
                      onClick={() =>
                        setSelectedQuestion({
                          ...selectedQuestion,
                          [index]: !selectedQuestion[index],
                        })
                      }
                    >
                      질문 {selectedQuestion[index] ? '닫기' : '보기'}
                    </Button>
                    {selectedQuestion[index] && (
                      <Grid marginTop='5px' sm={{ display: 'none' }}>
                        <div
                          style={{ color: Colors.grey3 }}
                          dangerouslySetInnerHTML={{
                            __html: purify.sanitize(q.content),
                          }}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
              {selectedQuestion && selectedQuestion[index] && (
                <Grid display='none' sm={{ display: 'flex' }}>
                  <div
                    style={{ color: Colors.grey3 }}
                    dangerouslySetInnerHTML={{
                      __html: purify.sanitize(q.content),
                    }}
                  />
                </Grid>
              )}
            </Fragment>
          ))}
        </Grid>
      )}
    </Grid>
  );
};

export default Questions;
